import { Link } from 'react-router-dom';
import {
  Compass, Clock, Target, ArrowRight, BookOpen, GraduationCap,
  Shield, Sparkles, Rocket, TrendingUp, Users,
} from 'lucide-react';

const DIFF_COLORS = {
  beginner: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  intermediate: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  advanced: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
  expert: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

// Each step links to a real article (/help-center?article=ID) or a real
// library guide (/learning-library?guide=ID). Mixing both is intentional —
// articles are reference, guides are workflows.
const PATHS = [
  {
    id: 'first-profitable-trade',
    title: 'Zero to first profitable trade',
    subtitle: 'The 14-day path from sign-up to your first real PnL — without blowing up',
    icon: Compass,
    accent: 'from-emerald-500 to-cyan-500',
    duration: '14 days',
    difficulty: 'beginner',
    audience: 'You have never used haythix. You may have traded crypto manually but never run a bot.',
    outcomes: [
      'Account secured and verified',
      'You understand what each bot type actually does',
      'A paper-traded strategy with at least 30 simulated trades behind it',
      'One live bot or smart trade running with strict risk rules',
    ],
    steps: [
      { kind: 'article', id: 'getting_started_signup_complete_guide', title: 'Sign up + verify your email', day: 'Day 1' },
      { kind: 'guide',   id: 'first-day-security-hardening',          title: 'First-day security hardening (15 min)', day: 'Day 1' },
      { kind: 'article', id: 'risk_rules_complete_guide',             title: 'Risk Rules: the account-wide guardrails', day: 'Day 2' },
      { kind: 'guide',   id: 'onboarding-zero-to-first-trade',        title: 'Zero to first trade guide', day: 'Day 3' },
      { kind: 'article', id: 'paper_trading_complete_guide',          title: 'Paper Trading: practice without risk', day: 'Days 4-10' },
      { kind: 'guide',   id: 'choose-the-right-bot',                  title: 'Choose the right bot for your style', day: 'Day 11' },
      { kind: 'article', id: 'smart_trade_complete_guide',            title: 'Smart Trade: your first live trade', day: 'Day 12' },
      { kind: 'guide',   id: 'paper-to-live-30-day-plan',             title: 'Paper-to-live promotion plan', day: 'Days 13-14' },
    ],
  },
  {
    id: 'security-first',
    title: 'Lock down before you fund',
    subtitle: 'Day-1 security playbook — finish this before you connect a single dollar',
    icon: Shield,
    accent: 'from-amber-500 to-red-500',
    duration: '1 hour',
    difficulty: 'beginner',
    audience: 'Anyone about to connect an exchange API key or fund the platform with real money.',
    outcomes: [
      'TOTP 2FA on with backup codes safely stored',
      'API keys scoped to least privilege with 90-day expiry',
      'All other sessions revoked, baseline login pattern recorded',
      'Withdrawal address whitelist configured',
    ],
    steps: [
      { kind: 'guide',   id: 'first-day-security-hardening',         title: 'The 15-minute hardening guide', day: 'Step 1' },
      { kind: 'article', id: 'security_settings_complete_guide',     title: 'Security Settings: every option explained', day: 'Step 2' },
      { kind: 'article', id: 'account_settings_complete_guide',      title: 'Account Settings: profile + privacy', day: 'Step 3' },
      { kind: 'article', id: 'trading_defaults_complete_guide',      title: 'Trading Defaults: slippage, gas, MEV protection', day: 'Step 4' },
    ],
  },
  {
    id: 'systematic-trader',
    title: 'Build a risk-managed trading system',
    subtitle: 'The 30-day path from "I think I have an edge" to a systematic backtested live strategy',
    icon: TrendingUp,
    accent: 'from-blue-500 to-violet-500',
    duration: '30 days',
    difficulty: 'intermediate',
    audience: 'You can read a chart. You want to stop discretionary trading and build something testable.',
    outcomes: [
      'A written strategy with explicit entry, exit, and risk rules',
      'Backtest results with realistic assumptions (fees, slippage, no look-ahead)',
      'Paper-traded for at least 30 sessions',
      'Live with position size calibrated to Kelly fraction and account-level risk caps',
    ],
    steps: [
      { kind: 'article', id: 'strategy_builder_complete_guide',           title: 'Strategy Builder: write your rules', day: 'Days 1-5' },
      { kind: 'article', id: 'backtest_complete_guide',                   title: 'Backtest with realistic assumptions', day: 'Days 6-10' },
      { kind: 'article', id: 'paper_trading_complete_guide',              title: 'Paper trade the strategy', day: 'Days 11-25' },
      { kind: 'article', id: 'kelly_criterion_complete_guide',            title: 'Size positions with Kelly Criterion', day: 'Day 26' },
      { kind: 'article', id: 'risk_rules_complete_guide',                 title: 'Set account-wide risk rules', day: 'Day 27' },
      { kind: 'guide',   id: 'build-risk-managed-trading-system',         title: 'Risk-managed system guide', day: 'Day 28' },
      { kind: 'guide',   id: 'paper-to-live-30-day-plan',                 title: 'Promote to live — gradually', day: 'Days 29-30' },
    ],
  },
  {
    id: 'creator-paid-channel',
    title: 'Build a paid signal channel',
    subtitle: 'The 60-day creator path from anonymous user to recurring monthly revenue',
    icon: Users,
    accent: 'from-pink-500 to-violet-500',
    duration: '60 days',
    difficulty: 'intermediate',
    audience: 'You have a track record (real or paper) and want to monetize calls to subscribers.',
    outcomes: [
      'Verified Creator badge earned',
      'A free public signal channel with engagement',
      'A paid tier launched with the 80/20 revenue split',
      'Renewal mechanics (USDC auto-renew or Stripe) running',
    ],
    steps: [
      { kind: 'guide',   id: 'first-day-security-hardening',              title: 'Lock down your account first', day: 'Day 1' },
      { kind: 'article', id: 'top_traders_complete_guide',                title: 'Study what real top traders do', day: 'Days 2-10' },
      { kind: 'article', id: 'forecast_accuracy_complete_guide',          title: 'Build a forecast-accuracy track record', day: 'Days 11-20' },
      { kind: 'article', id: 'signal_channels_complete_guide',            title: 'Launch a free signal channel', day: 'Days 21-35' },
      { kind: 'article', id: 'paid_channels_complete_guide',              title: 'Convert to paid — 80/20 split mechanics', day: 'Days 36-45' },
      { kind: 'guide',   id: 'creator-zero-to-first-paid-subscriber',     title: 'First paid subscriber playbook', day: 'Days 46-60' },
    ],
  },
  {
    id: 'token-launch',
    title: '90-day token launch playbook',
    subtitle: 'The creator/founder path from tokenomics design to a real, traded launch',
    icon: Rocket,
    accent: 'from-violet-500 to-indigo-500',
    duration: '90 days',
    difficulty: 'advanced',
    audience: 'You are launching a token. You want a structured path that does not collapse at the LP step.',
    outcomes: [
      'Tokenomics designed and stress-tested',
      'Contract audited and ready',
      'Initial liquidity provisioned with locking',
      'Listing requirements met for the venues you want',
      'A real launch with monitored volume health, not a one-day wonder',
    ],
    steps: [
      { kind: 'article', id: 'tokenomics_studio_complete_guide',         title: 'Design tokenomics with the Studio', day: 'Weeks 1-2' },
      { kind: 'guide',   id: 'tokenomics-studio-issuer-walkthrough',     title: 'Issuer walkthrough', day: 'Weeks 2-3' },
      { kind: 'article', id: 'dex_liquidity_optimizer_complete_guide',   title: 'Plan initial DEX liquidity', day: 'Weeks 4-5' },
      { kind: 'article', id: 'listing_readiness_complete_guide',         title: 'Listing readiness checklist', day: 'Weeks 6-8' },
      { kind: 'article', id: 'launch_volume_forecaster_complete_guide',  title: 'Forecast realistic launch volume', day: 'Week 9' },
      { kind: 'article', id: 'launch_roadmap_complete_guide',            title: 'Build the launch roadmap', day: 'Weeks 10-11' },
      { kind: 'article', id: 'volume_health_complete_guide',             title: 'Monitor volume health post-launch', day: 'Weeks 12-13' },
      { kind: 'guide',   id: 'launch-token-90-day-playbook',             title: 'Full 90-day playbook', day: 'Reference' },
    ],
  },
];

function deepLink(step) {
  return step.kind === 'guide'
    ? `/learning-library?guide=${step.id}`
    : `/help-center?article=${step.id}`;
}

export default function LearningPathsPage() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 via-pink-500 to-violet-500 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          Learning Paths
        </h1>
        <p className="text-gray-400 text-sm mt-2 max-w-2xl">
          Curated sequences of articles and guides for the five most common journeys on haythix.
          Each path tells you what to read, in what order, with realistic timelines.
        </p>
      </div>

      {/* Path cards */}
      <div className="space-y-6">
        {PATHS.map((path) => {
          const Icon = path.icon;
          const firstStep = path.steps[0];
          return (
            <div
              key={path.id}
              className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden"
            >
              {/* Header bar */}
              <div className={`bg-gradient-to-r ${path.accent} px-6 py-4`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{path.title}</h2>
                      <p className="text-white/80 text-sm mt-0.5">{path.subtitle}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs text-white/90 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {path.duration}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${DIFF_COLORS[path.difficulty]}`}>
                      {path.difficulty}
                    </span>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Audience + outcomes */}
                  <div className="lg:col-span-1 space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1.5">Who this is for</p>
                      <p className="text-sm text-gray-300 leading-relaxed">{path.audience}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1.5">What you&apos;ll achieve</p>
                      <ul className="space-y-1.5">
                        {path.outcomes.map((o, i) => (
                          <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                            <Target className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span>{o}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="lg:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
                      The path — {path.steps.length} steps
                    </p>
                    <ol className="space-y-2">
                      {path.steps.map((step, i) => (
                        <li key={i}>
                          <Link
                            to={deepLink(step)}
                            className="group flex items-center gap-3 p-3 rounded-xl bg-dark-700/40 hover:bg-dark-700 border border-dark-600/40 hover:border-amber-500/30 transition-all"
                          >
                            <span className="w-7 h-7 rounded-lg bg-dark-800 border border-dark-600 flex items-center justify-center text-xs font-bold text-gray-400 group-hover:text-amber-400 flex-shrink-0">
                              {i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white font-medium truncate">{step.title}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                                <span className="flex items-center gap-1">
                                  {step.kind === 'guide'
                                    ? <><BookOpen className="w-3 h-3" /> Guide</>
                                    : <><Sparkles className="w-3 h-3" /> Article</>}
                                </span>
                                <span>·</span>
                                <span>{step.day}</span>
                              </p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                          </Link>
                        </li>
                      ))}
                    </ol>

                    {/* Start here button */}
                    <Link
                      to={deepLink(firstStep)}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-dark-900 font-semibold text-sm transition-colors"
                    >
                      Start here <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer — pointer back to library/help */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/help-center"
          className="bg-dark-800 hover:bg-dark-700 rounded-2xl border border-dark-700/50 p-5 flex items-center gap-3 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">Browse all articles</p>
            <p className="text-xs text-gray-500">Full reference for every feature</p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-500" />
        </Link>
        <Link
          to="/learning-library"
          className="bg-dark-800 hover:bg-dark-700 rounded-2xl border border-dark-700/50 p-5 flex items-center gap-3 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">Browse all guides</p>
            <p className="text-xs text-gray-500">Workflow-oriented step-by-step playbooks</p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-500" />
        </Link>
      </div>
    </div>
  );
}
